/* eslint-disable require-atomic-updates */
import type { AuthenticatedSession } from '@ps/web-core-store';
import { Cookie as CookieNames, FeatureFlag, LoginSource } from '@ps/web-core-store';
import Cookies from 'universal-cookie';
import Url, { qs } from 'url-parse';

import { call, get } from '@swf/core/api/embedded';
import type { EffectParams } from '@swf/core/store';
import { getState, select } from '@swf/core/store';
import { clearAll } from '@swf/core/utils/auth';

import { loginSuccess } from './utils';

interface Query {
  [key: string]: string;
}

const cookies = new Cookies();

const getParam = (
  name: string,
  cookie: CookieNames,
  embedded: boolean,
  hash: Query,
  hashName?: string
) => {
  if (embedded) {
    return get(['auth', name]);
  }
  return hash[`ram_${hashName || name}`] || cookies.get(cookie);
};

const verifySession = async (
  payload: undefined,
  { dispatch, request, rootState }: EffectParams
) => {
  const parsed = qs.parse((rootState.location.hash || '').substring(1)) as Query;
  const isFromHash = Object.keys(parsed).some((val) => val.startsWith('ram_'));
  const isEmbedded = select.device.isEmbedded(rootState);
  const userWebId = getParam('webId', CookieNames.WEB_ID, isEmbedded, parsed, 'u');
  const token = getParam('token', CookieNames.TOKEN, isEmbedded, parsed);
  const signature = getParam('signature', CookieNames.SIGNATURE, isEmbedded, parsed);
  const currentSession = select.session.getLegacySession(rootState);
  const loginSource = LoginSource.VerifySession;
  const useRam1 = select.feature.check(getState(), FeatureFlag.UseRam1);
  const isIdpSecondaryLoginEnabled = select.feature.check(
    rootState,
    FeatureFlag.IdpSecondaryLoginEnabled
  );

  if (useRam1) {
    return;
  }

  if (isFromHash) {
    const url = new Url(rootState.location.href, true);
    delete parsed.ram_token;
    delete parsed.ram_signature;
    delete parsed.ram_u;
    url.set('hash', Url.qs.stringify(parsed));
    window.history.pushState({}, '', url.href);
    await dispatch.location.update(url);
  }

  if (
    !token ||
    !signature ||
    !userWebId ||
    (currentSession.token === token && currentSession.signature === signature)
  ) {
    await dispatch.session.update({ authSource: 'client', serverAuth: false });
    return;
  }

  try {
    const session = { token, signature, userWebId, loginSource } as AuthenticatedSession;
    await loginSuccess(session, { dispatch, request });
    if (isIdpSecondaryLoginEnabled) {
      await dispatch.auth.idpLoginSuccess({ loginSource: LoginSource.VerifySession });
    }
  } catch (error) {
    clearAll(dispatch);
    call(['auth', 'login']);
  }
};

export default verifySession;
